import { useLayoutEffect } from 'react'
import { Link } from 'react-router-dom'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'

export default function PrivacyPage() {
  useLayoutEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      const offset = headerH + navH
      document.documentElement.style.setProperty('--header-nav-height', `${offset}px`)
    }
    measure()
    requestAnimationFrame(measure)
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  return (
    <>
      <div className="bg-gray-50 min-h-screen text-[#232323]">
        <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
          <ContentColumn anchor="privacy-hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="font-sans text-[42px] sm:text-[48px] md:text-[56px] font-bold text-gray-50 mb-6 leading-[1.3] tracking-[-0.02em]">
                Privacy Policy
              </h1>
              <p className="text-[21px] leading-[1.58] text-gray-50/85 font-body mb-10">
                Your privacy is important to us.
              </p>
            </div>
          </ContentColumn>
        </section>

        <section className="border-b border-border-subtle bg-white">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4">
            <nav aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-label font-body text-gray-900/60">
                <li>
                  <Link to="/" className="transition-colors hover:text-gray-900 focus:text-gray-900 focus:outline-none">
                    Home
                  </Link>
                </li>
                <li className="font-heading text-gray-900">/</li>
                <li className="font-heading text-gray-900" aria-current="page">
                  Privacy Policy
                </li>
              </ol>
            </nav>
          </div>
        </section>

        <section className="bg-white py-20">
          <ContentColumn anchor="privacy-main" className="max-w-4xl mx-auto px-4">
            <div className="prose prose-lg max-w-none">
              <div className="bg-white border-2 border-gray-200 rounded-smooth-lg p-8 sm:p-12 shadow-sm">
                <h2 className="font-heading text-3xl font-bold uppercase text-gray-900 mb-6">
                  Privacy Policy
                </h2>

                <div className="space-y-6 font-body text-base text-[#232323] leading-relaxed">
                  <div>
                    <p className="mb-4">
                      <strong>Last Updated:</strong> July 2026
                    </p>
                    <p className="mb-4">
                      QUEEK LIMITED ("we", "our", or "us") values your privacy. This Privacy Policy explains how we handle information when you visit gazar.dev, a brand and portfolio operated by QUEEK LIMITED, including when you create an account or purchase a course.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">
                      Information We Collect
                    </h3>
                    <p className="mb-4">
                      We collect only what we need to run the Site and your account:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                      <li><strong>Account information</strong> — your email address, optional name, and a securely hashed password when you register. We also record when you accepted these terms and whether you opted in to marketing email.</li>
                      <li><strong>Purchase information</strong> — courses you buy and the resulting entitlements. Card payments are processed by Stripe; we never see or store your full card details.</li>
                      <li>Basic usage data through standard web server logs (IP address, browser type, pages visited)</li>
                      <li>Information you voluntarily provide when contacting us via email</li>
                      <li>Cookies and similar technologies used for sign-in and functionality — see our <Link to="/cookies" className="text-[#2d5016] hover:text-[#1a5a3a] hover:underline font-semibold">Cookie Policy</Link></li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">
                      How We Use Information
                    </h3>
                    <p className="mb-4">
                      Information we collect is used solely for:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                      <li>Providing your account, courses, and purchases</li>
                      <li>Processing payments (via Stripe) and granting access to paid content</li>
                      <li>Sending product updates or marketing only if you opted in (you can opt out at any time)</li>
                      <li>Improving website functionality and user experience</li>
                      <li>Responding to inquiries and communications</li>
                      <li>Ensuring website security and preventing abuse</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">
                      Data Sharing
                    </h3>
                    <p className="mb-4">
                      We do not sell, trade, or rent your personal information to third parties. We may share information only when required by law or to protect our rights.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">
                      Contact Us
                    </h3>
                    <p className="mb-4">
                      If you have questions about this Privacy Policy, please contact us at:{' '}
                      <a
                        href="mailto:me@gazar.dev"
                        className="text-[#2d5016] hover:text-[#1a5a3a] hover:underline font-semibold"
                      >
                        me@gazar.dev
                      </a>
                    </p>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-gray-900 mb-3 mt-8">
                      Changes to This Policy
                    </h3>
                    <p className="mb-4">
                      We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ContentColumn>
        </section>
      </div>
    </>
  )
}
